// Pages that are one page in several languages, and where a problem that many pages share is looked after.
// Pure code. Two things are decided here, both from what the website itself says (the address and the declared
// language of each page), never guessed from a name:
//  - the language versions of a page: the same address once the language is taken out of it (`/en/contact`,
//    `/ca/contact` and `/contact` are the same page) and different declared languages;
//  - the owner of a problem shared by several pages: the home page when it is one of them, otherwise the first of
//    them, so the problem is listed once and the rest only point to it.

export interface PageRef {
  key: string;
  url: string;
  lang: string | null;
}

/** The primary language subtag of a page (`es-ES` → `es`), or null when it does not say. */
export const primaryLanguage = (lang: string | null | undefined): string | null => {
  const code = lang?.trim().toLowerCase().split(/[-_]/)[0];
  return code && /^[a-z]{2,3}$/.test(code) ? code : null;
};

const LANGUAGE_PARAMS = new Set(['lang', 'hl', 'language', 'locale', 'l']);

interface Parsed {
  /** The address without the language: what two versions of a page have in common. */
  base: string;
  path: string;
  /** The language was taken out of the address: this is a version in a language, not the website's default. */
  stripped: boolean;
}

/** The address of a page without its language (a first path segment or a query parameter that says it). */
export function languageFree(url: string, lang: string | null): Parsed | null {
  const code = primaryLanguage(lang);
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const segments = parsed.pathname.split('/').filter(Boolean);
  let stripped = false;
  if (
    code &&
    segments[0] &&
    primaryLanguage(segments[0]) === code &&
    /^[a-z]{2,3}(?:[-_][a-z]{2,4})?$/i.test(segments[0])
  ) {
    segments.shift();
    stripped = true;
  }
  const query = [...parsed.searchParams]
    .filter(([name, value]) => {
      const isLanguage = LANGUAGE_PARAMS.has(name.toLowerCase()) && code && primaryLanguage(value) === code;
      if (isLanguage) stripped = true;
      return !isLanguage;
    })
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, value]) => `${name}=${value}`)
    .join('&');
  const path = `/${segments.join('/')}`;
  return { base: `${parsed.hostname.toLowerCase()}${path}${query ? `?${query}` : ''}`, path, stripped };
}

export interface PageFamily<T extends PageRef = PageRef> {
  /** The key of the main version: the website's default language when it can be told, else the first. */
  key: string;
  /** Every version, the main one first. One member: a page that has no other language. */
  members: T[];
}

/**
 * The pages grouped by language: versions of the same page (same address without the language, different declared
 * languages) are one family; every other page is its own. In the order the pages came in.
 */
export function pageFamilies<T extends PageRef>(pages: readonly T[]): PageFamily<T>[] {
  const parsed = new Map(pages.map((page) => [page.key, languageFree(page.url, page.lang)]));
  const byBase = new Map<string, T[]>();
  for (const page of pages) {
    const info = parsed.get(page.key);
    if (!info || !primaryLanguage(page.lang)) continue;
    byBase.set(info.base, [...(byBase.get(info.base) ?? []), page]);
  }
  const familyOf = new Map<string, PageFamily<T>>();
  for (const members of byBase.values()) {
    // One version per language: two pages that say the same language are not translations of each other.
    const perLanguage = new Map<string, T>();
    for (const page of members) {
      const code = primaryLanguage(page.lang) as string;
      const before = perLanguage.get(code);
      if (!before || (parsed.get(before.key)?.stripped && !parsed.get(page.key)?.stripped))
        perLanguage.set(code, page);
    }
    if (perLanguage.size < 2) continue;
    const ordered = [...perLanguage.values()].sort(
      (a, b) =>
        Number(parsed.get(a.key)?.stripped) - Number(parsed.get(b.key)?.stripped) ||
        pages.indexOf(a) - pages.indexOf(b),
    );
    const family: PageFamily<T> = { key: ordered[0]!.key, members: ordered };
    for (const page of ordered) familyOf.set(page.key, family);
  }
  const done = new Set<PageFamily<T>>();
  const out: PageFamily<T>[] = [];
  for (const page of pages) {
    const family = familyOf.get(page.key);
    if (!family) {
      out.push({ key: page.key, members: [page] });
    } else if (!done.has(family)) {
      done.add(family);
      out.push(family);
    }
  }
  return out;
}

/** The home page of a language version or of the website: its address, without the language, is the root. */
export const isHomePage = (page: PageRef): boolean => languageFree(page.url, page.lang)?.path === '/';

export type OwnerKind = 'home' | 'first';

/** The language most of the pages are in: the website's main one, when the address does not say it. */
function mainLanguage(pages: readonly PageRef[]): string | null {
  const counts = new Map<string, number>();
  for (const page of pages) {
    const code = primaryLanguage(page.lang);
    if (code) counts.set(code, (counts.get(code) ?? 0) + 1);
  }
  return [...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]?.[0] ?? null;
}

/**
 * Where a problem that several pages share is looked after: the home page when it is one of them (the website's own
 * before the language versions of it), otherwise the first of them. `first` means the home page was not among
 * them, so the problem is listed in the website's global section and is not tied to the home page.
 */
export function ownerOf(
  pageKeys: readonly string[],
  pages: readonly PageRef[],
): { key: string; kind: OwnerKind } {
  const byKey = new Map(pages.map((page) => [page.key, page]));
  const candidates = pageKeys.flatMap((key) => {
    const page = byKey.get(key);
    return page ? [page] : [];
  });
  const main = mainLanguage(pages);
  const homes = candidates
    .filter(isHomePage)
    .sort(
      (a, b) =>
        Number(languageFree(a.url, a.lang)?.stripped) - Number(languageFree(b.url, b.lang)?.stripped) ||
        Number(primaryLanguage(b.lang) === main) - Number(primaryLanguage(a.lang) === main) ||
        a.url.localeCompare(b.url),
    );
  if (homes[0]) return { key: homes[0].key, kind: 'home' };
  const first = [...candidates].sort(
    (a, b) =>
      (languageFree(a.url, a.lang)?.path.split('/').length ?? 0) -
        (languageFree(b.url, b.lang)?.path.split('/').length ?? 0) || a.url.localeCompare(b.url),
  )[0];
  return { key: first?.key ?? pageKeys[0] ?? '', kind: 'first' };
}

export type GlobalProgress =
  | 'sin-aplicar'
  | 'aplicada-en-parte'
  | 'aplicada'
  | 'verificada-en-parte'
  | 'verificada';

/**
 * Where the correction of a problem shared by several pages stands, from the page-by-page records: applying it is
 * the team's work, verifying it is an analysis (or a person) that no longer finds it. A shared problem is only
 * settled when every page it affects was checked: one page not checked leaves it partly verified.
 */
export function globalProgress(
  pages: readonly { work: string | null; check: string | null }[],
): GlobalProgress {
  const verified = pages.filter((page) => page.check === 'no-detectado' || page.work === 'archivada').length;
  if (pages.length > 0 && verified === pages.length) return 'verificada';
  if (verified > 0) return 'verificada-en-parte';
  const applied = pages.filter((page) => page.work === 'aplicada').length;
  if (pages.length > 0 && applied === pages.length) return 'aplicada';
  return applied > 0 ? 'aplicada-en-parte' : 'sin-aplicar';
}
